import { forwardRef } from 'react'
import { StagedPhotoGrid, StagedPhotoGridHandle } from './StagedPhotoGrid'
import { generatePassengerPreferencePhotoS3Key } from '@/lib/hooks/s3'
import {
  usePassengerAddPreferencePhoto,
  usePassengerRemovePreferencePhoto,
} from '@/lib/hooks/passenger'

interface PaxPreferencePhotosProps {
  // Preference key the photos belong to (e.g. foodPreferences). Sent to the
  // backend and folded into the S3 key namespace.
  preference: string
  // S3 keys already persisted for this preference.
  s3Keys: string[]
  readOnly?: boolean
  onPendingChange?: (count: number) => void
  trigger?: 'tile' | 'icon'
}

export type PaxPreferencePhotosHandle = StagedPhotoGridHandle

// Thin binding of StagedPhotoGrid to the passenger preference-photo endpoints.
export const PaxPreferencePhotos = forwardRef<PaxPreferencePhotosHandle, PaxPreferencePhotosProps>(
  function PaxPreferencePhotos(
    { preference, s3Keys, readOnly = false, onPendingChange, trigger },
    ref
  ) {
    const addPhoto = usePassengerAddPreferencePhoto()
    const removePhoto = usePassengerRemovePreferencePhoto()

    return (
      <StagedPhotoGrid
        ref={ref}
        s3Keys={s3Keys}
        readOnly={readOnly}
        onPendingChange={onPendingChange}
        trigger={trigger}
        generateS3Key={(entityId, fileName) =>
          generatePassengerPreferencePhotoS3Key(entityId, preference, fileName)
        }
        addPhoto={(entityId, s3Key) =>
          addPhoto.mutateAsync({ id: entityId, preference, s3Key } as never)
        }
        removePhoto={(entityId, s3Key) =>
          removePhoto.mutateAsync({ id: entityId, preference, s3Key } as never)
        }
      />
    )
  }
)
